// tooltip.ts — the dark tooltip (§7.14) for every [data-tip] element in the
// app. ONE delegated listener + ONE fixed element: no per-element handlers,
// no React state, and it escapes overflow:hidden panel headers (a CSS
// ::after tooltip would be clipped there). Call installTooltips() once.
let installed = false;

export function installTooltips(delay = 400): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  const tip = document.createElement('div');
  tip.className = 'qt-tooltip';
  tip.setAttribute('role', 'tooltip');
  document.body.appendChild(tip);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let current: HTMLElement | null = null;

  const hide = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    current = null;
    tip.dataset.show = 'false';
  };
  const show = (el: HTMLElement) => {
    const text = el.dataset.tip;
    if (!text) return;
    tip.textContent = text;
    tip.dataset.show = 'false';
    const r = el.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const pos = el.dataset.tipPos ?? 'bottom';
    let left = r.left + r.width / 2 - t.width / 2;
    let top = r.bottom + 6;
    if (pos === 'right') { left = r.right + 8; top = r.top + r.height / 2 - t.height / 2; }
    if (pos === 'top' || top + t.height > window.innerHeight - 4) top = r.top - t.height - 6;
    left = Math.max(4, Math.min(window.innerWidth - t.width - 4, left));
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
    tip.dataset.show = 'true';
  };

  document.addEventListener('pointerover', (e) => {
    // A native `title` becomes a data-tip the first time the pointer reaches it, so
    // every hint in both apps shows the same dark tooltip (§7.14) - no native yellow box.
    const titled = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('[title]');
    if (titled && !titled.dataset.tip && titled.getAttribute('title')) {
      titled.dataset.tip = titled.getAttribute('title')!;
      titled.removeAttribute('title');
    }
    const el = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-tip]') ?? null;
    if (el === current) return;
    hide();
    if (!el) return;
    current = el;
    timer = setTimeout(() => show(el), delay);
  }, { passive: true });
  document.addEventListener('pointerdown', hide, { passive: true, capture: true });
  document.addEventListener('scroll', hide, { passive: true, capture: true });
  window.addEventListener('blur', hide);
}
